import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Stack,
  Button,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  Skeleton,
  SkeletonText,
  Heading,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import DataSummaryView from './DataSummaryView';
import GoalInsightsView from './GoalInsightsView';
import VisualizationView from './VisualizationView';
import { react_api } from '../../../../config';

// // Reusable title component
// const Title = ({ children, as = "h2", size = "xl", ...props }) => (
//   <Text
//     as={as}
//     fontWeight="bold"
//     fontSize={size === "xl" ? "2xl" : size === "lg" ? "xl" : "lg"}
//     mb={2}
//     {...props}
//   >
//     {children}
//   </Text>
// );

const Nova = ({ directData }) => {
  // Local state management - no global store
  const [currentSummary, setCurrentSummary] = useState(null);
  const [isProcessingData, setIsProcessingData] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [username, setUsername] = useState('');
  const [bearerToken, setBearerToken] = useState('');

  // Process direct data when provided
  useEffect(() => {
    if (directData) {
      processSummaryData();
    }
  }, [directData]);

  // Process the summary data
  const processSummaryData = async () => {
    if (!directData) return;
    
    setIsProcessingData(true);
    setError(null);
    setIsInitialLoading(true);
    
    try {
      // Make direct API call using axios
      const response = await axios.post(`${react_api}/externalService/novaServices`, {
        username: directData.username,
        BearerToken: directData.BearerToken,
        service: 0,
        request: directData.request
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Process response
      if (response.data && response.data.request && response.data.request.summary) {
        setCurrentSummary({
          summary: response.data.request.summary
        });
        
        // Save authentication data for reuse in subsequent calls
        if (response.data.username) {
          setUsername(response.data.username);
        } else {
          setUsername(directData.username); 
        }
        
        if (response.data.bearer_token) {
          setBearerToken(response.data.bearer_token);
        } else if (response.data.BearerToken) {
          setBearerToken(response.data.BearerToken);
        } else {
          setBearerToken(directData.bearer_token); 
        }
        
        setError(null);
      } else {
        setError("Invalid response from server. Expected data.request.summary structure.");
        console.error('Unexpected response structure:', response.data);
      }
    } catch (err) {
      console.error('Error processing summary data:', err);
      setError(err.response?.data?.message || err.message || "Error processing data");
    } finally {
      setIsProcessingData(false);
      setIsInitialLoading(false);
    }
  };
  
  // Retry handler
  const handleRetry = () => {
    processSummaryData();
  };

  // Check for errors
  const hasError = error !== null;

  // Determine if we should show the summary
  const shouldShowSummary = currentSummary && !hasError && !isProcessingData && !isInitialLoading;

  // Define colors within the Nova component to make it self-contained
  const themeColors = {
    navbarBg: '#0b4e5aff',
    fontColor: '#0b4e5aff',
    borderColor: '#178da1ff',
    buttonColor: '#083c46ff',
    iconColor: '#9acd32',  
    titleIconColor: '#9acd32',
    servicesBg: '#f6f3ec'
  };

  // Tab change handler
  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  // Goal/insight selection handler
  const handleGoalSelect = (goal) => {
    setSelectedGoal(goal);
    
    if (goal) {
      // Automatically switch to the Visualization tab (index 2)
      setActiveTab(2);
    }
  };

  return (
    <Box width="100%" bg={themeColors.servicesBg} p={4} borderRadius="lg" boxShadow="md">
      <Box mb={4} p={3} bg={themeColors.navbarBg} color="white" borderRadius="md" boxShadow="sm">
        <Heading as="h2" size="md">
          <Box as="span" color={themeColors.titleIconColor} mr={2}>📊</Box>
          Nova Data Explorer
        </Heading>
      </Box>
      
      {/* Show loading skeleton during initial load or when processing data */}
      {(isProcessingData || isInitialLoading) && (
        <Box p={4} borderWidth="1px" borderColor={themeColors.borderColor} borderRadius="md" shadow="md" bg="white" width="100%">
          <Flex align="center" justify="space-between" mb={4}>
            <Skeleton height="32px" width="180px" />
            <Skeleton height="24px" width="24px" borderRadius="full" />
          </Flex>
          <Divider mb={4} />

          <Stack spacing={4}>
            <SkeletonText mt={2} noOfLines={1} spacing={4} skeletonHeight={4} width="60%" />
            
            <Box p={2} bg="gray.50" borderRadius="md">
              <SkeletonText mt={2} noOfLines={4} spacing={4} skeletonHeight={3} />
            </Box>
            
            <Flex>
              <Skeleton height="20px" width="100px" mr={2} />
              <Skeleton height="20px" width="80px" />
            </Flex>
            
            <Stack spacing={2}>
              <Skeleton height="20px" width="40%" />
              <Skeleton height="20px" width="70%" />
              <Skeleton height="20px" width="50%" />
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Show error message if there's an error */}
      {hasError && (
        <Box p={4} mb={4}>
          <Alert status="error" borderRadius="md" borderWidth="1px" borderColor={themeColors.borderColor}>
            <AlertIcon color="red.500" />
            <Box flex="1">
              <Text fontWeight={600} color={themeColors.fontColor}>Error</Text>
              <Text color={themeColors.fontColor}>{error}</Text>
            </Box>
            <Button
              onClick={handleRetry}
              leftIcon={<RepeatIcon color={themeColors.iconColor} />}
              bg={themeColors.buttonColor}
              color="white"
              _hover={{ bg: '#0a4956' }}
              variant="solid"
              size="sm"
              ml={2}
              borderRadius="md"
            >
              Retry
            </Button>
          </Alert>
        </Box>
      )}

      {/* Sistema di tab per navigare tra Summary e Insights */}
      {!hasError && !isProcessingData && !isInitialLoading && shouldShowSummary && currentSummary && (
        <Tabs 
          variant="enclosed" 
          colorScheme="teal" 
          index={activeTab} 
          onChange={handleTabChange}
          borderColor={themeColors.borderColor}
        >
          <TabList mb="-1px">
            <Tab 
              _selected={{ 
                color: 'white', 
                bg: themeColors.navbarBg,
                borderColor: themeColors.borderColor,
                borderBottomColor: themeColors.navbarBg
              }}
              color={themeColors.fontColor}
              fontWeight="medium"
            >
              Data Summary
            </Tab>
            <Tab 
              _selected={{ 
                color: 'white', 
                bg: themeColors.navbarBg,
                borderColor: themeColors.borderColor,
                borderBottomColor: themeColors.navbarBg
              }}
              color={themeColors.fontColor}
              fontWeight="medium"
            >
              Insights
            </Tab>
            {selectedGoal && (
              <Tab 
                _selected={{ 
                  color: 'white', 
                  bg: themeColors.navbarBg,
                  borderColor: themeColors.borderColor,
                  borderBottomColor: themeColors.navbarBg
                }}
                color={themeColors.fontColor}
                fontWeight="medium"
              >
                Visualization
              </Tab>
            )}
          </TabList>
          
          <TabPanels borderWidth="1px" borderColor={themeColors.borderColor} borderRadius="md" bg="white">
            {/* Panel per Data Summary */}
            <TabPanel p={4}>
              <Box mb={4}>
                <Flex alignItems="center" mb={2}>
                  <Text fontSize="lg" fontWeight="bold" color={themeColors.fontColor}>Dataset:</Text>
                  <Text fontSize="lg" ml={2}>{currentSummary?.summary?.dataset_name}</Text>
                </Flex>
                {currentSummary?.summary?.dataset_description && (
                  <Box mt={2}>
                    <Text fontSize="md" fontWeight="bold" color={themeColors.fontColor}>Description:</Text>
                    <Text fontSize="md" mt={1}>{currentSummary?.summary?.dataset_description}</Text>
                  </Box>
                )}
              </Box>
              <Divider my={4} />
              <DataSummaryView summary={currentSummary?.summary} colors={themeColors} />
            </TabPanel>
            
            {/* Panel per Insights */}
            <TabPanel p={4}>
              <GoalInsightsView 
                currentSummary={currentSummary} 
                onGoalSelect={handleGoalSelect} 
                colors={themeColors}
                username={username}
                bearerToken={bearerToken}
              />
            </TabPanel>
            
            {/* Panel per Insight Detail */}
            {selectedGoal && (
              <TabPanel>
                <VisualizationView 
                  selectedGoal={selectedGoal}
                  currentSummary={currentSummary}
                  colors={themeColors}
                  username={username}
                  bearerToken={bearerToken}
                />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};

export default Nova;
